import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Axios from 'axios';
import ActionCable from 'actioncable';
import DriverDropDown from '../groupedTrips/enhancedGroupedTripsComponents/DriverDropDown';
import { showToast } from '../toasts';

const socketUrl = process.env.REACT_APP_SECURE_ACTION_CABLE_URL;
const SAVING_TIMEOUT_MS = 30000;
const OVERLAY_CONTAINER_SELECTOR = '[data-testid="driver-details-card"]';

const ControlRoomDriverAssignment = ({
  groupId,
  companyId,
  groupDetails,
  bound,
  dateTime,
}) => {
  const [drivers, setDrivers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [overlayContainer, setOverlayContainer] = useState(null);
  const driverRef = useRef(null);

  useEffect(() => {
    const container = document.querySelector(OVERLAY_CONTAINER_SELECTOR);
    if (!container) return;
    const currentPosition = getComputedStyle(container).position;
    if (!currentPosition || currentPosition === 'static') {
      container.style.position = 'relative';
    }
    setOverlayContainer(container);
  }, []);

  useEffect(() => {
    Axios.get(`/vehicles/drivers?company=${companyId}&date=${groupDetails?.date || ''}`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => setDrivers(res.data))
      .catch((err) => console.error('Failed to fetch drivers for control_room', err));
  }, [companyId, groupDetails?.date]);

  useEffect(() => {
    const cable = ActionCable.createConsumer(socketUrl);
    const channel = cable.subscriptions.create('RouteFinishedChannel', {
      received: (data) => {
        if (data.message === 'assignment_error' && data.id === groupId) {
          setSaving(false);
          showToast('error', data.error);
        }
      },
    });
    return () => {
      channel.unsubscribe();
      cable.disconnect();
    };
  }, [groupId]);

  // Safety net: if the async worker never broadcasts back (Sidekiq down, dropped
  // WebSocket, silent failure) the overlay would hang. Auto-clear after 30s and
  // tell the user to refresh — success normally unmounts this island via the
  // Turbo Stream card-replace long before this fires.
  useEffect(() => {
    if (!saving) return undefined;
    const timer = setTimeout(() => {
      setSaving(false);
      showToast(
        'error',
        'Assignment is taking longer than expected. Please refresh the page.'
      );
    }, SAVING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [saving]);

  const handleAssign = (selectedDriver, gid, tripReason, forceAssign) => {
    Axios.put(
      `/grouped_trips/${gid}/assign_vehicle`,
      {
        vehicle: selectedDriver.id,
        trip_reason: tripReason,
        force_assign: forceAssign,
      },
      { headers: { Accept: 'application/json' } }
    ).catch((err) => {
      console.error('assign_vehicle failed', err);
      setSaving(false);
      showToast('error', 'Failed to assign driver. Please try again.');
    });
  };

  if (!drivers) {
    return <div className="mt-3 text-xs text-gray-700">Loading drivers…</div>;
  }

  const overlay = saving ? (
    <div
      data-testid="control-room-driver-assigning-overlay"
      className="absolute inset-0 bg-black opacity-50 rounded flex flex-col items-center justify-center z-10">
      <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-8 w-8 mb-2"></div>
      <span className="text-white text-xs font-semibold">
        Assigning driver…
      </span>
    </div>
  ) : null;

  return (
    <>
      <DriverDropDown
        drivers={drivers}
        groupId={groupId}
        groupDetails={groupDetails}
        updateDriver={handleAssign}
        setLoadingCard={setSaving}
        dateTime={dateTime}
        bound={bound}
        trips={[]}
        tripsLength={1}
        driverRef={driverRef}
        assignAllDrivers={0}
        tripReasonModalPortalTarget="turbo-frame#selected_trip"
      />
      {overlay && overlayContainer ? createPortal(overlay, overlayContainer) : overlay}
    </>
  );
};

export default ControlRoomDriverAssignment;
